/**
 * appointmentRequestOwner.js — PROD-192. cases.appointment_request belongs to the
 * SERVER. These two helpers are the only shapes in which the client may touch it.
 *
 * ★★★ WHY. PROD-186 measured the patient app PUTting a client-built 4-key object
 *   (status 'confirmed') over the request send_case_to_doctor had just written, so the
 *   doctor's accept never confirmed anything. Five more paths PUT a client-built
 *   request the same way (ShareScreen handleMDOnboardComplete, the DeliverScreen
 *   observation bridge and reopen, CaseHome reschedule, MDCaseOnboarding — the last one
 *   never routed and deleted in v242.239.0, R36 / PROD-211). Each one
 *   now either:
 *     (a) calls a route that writes appointment_request itself, then MIRRORS the
 *         server's value locally (readServerAppointmentRequest + updateCase, which
 *         never calls the API), or
 *     (b) where the client PUT is the only writer, MERGES its change onto a FRESH read
 *         of the server's value (mergeOntoServerRequest). The merge never carries a
 *         `status`: the status stays whatever the server last wrote.
 *
 * Gate: scripts/check-appointment-request-owner.mjs enumerates every server writer
 * of appointmentRequest/appointment_request in src/ by AST and holds each one to an
 * allowlist of intended-null writes or to shape (b).
 */
import { casesAPI } from '../services/api'

/**
 * Read the case back and return the server's own appointment_request, parsed.
 * Returns null when the case has none. Throws when the read itself fails, so a
 * caller can tell "the server has no request" from "I could not see".
 */
export async function readServerAppointmentRequest(caseId) {
  const fresh = await casesAPI.get(caseId)
  const raw = fresh?.appointmentRequest ?? fresh?.appointment_request ?? null
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) } catch { return null }
  }
  return raw && typeof raw === 'object' ? raw : null
}

/**
 * The client's change laid over the server's current request. `status` is
 * dropped from the patch: the client never writes a status the server did not
 * produce.
 */
export function mergeOntoServerRequest(serverRequest, patch) {
  const { status: _clientStatusIsNeverWritten, ...rest } = patch || {}
  return { ...(serverRequest || {}), ...rest }
}
